import { ResumeModal } from "./ResumeModal";
export { ResumeModal } from "./ResumeModal";
import { useRef, useState, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { Pencil, Eye, Camera, FileText, Download, Plus, X, Trash2, Loader2, EllipsisVertical, ImagePlus } from "lucide-react";
import { SidebarIdentityCard } from "./SidebarIdentityCard";
import { LocationPin } from "./LocationPin";
import { AccountVerificationBadge } from "./AccountVerificationBadge";
import { accountVerificationFrom } from "../lib/accountVerification";
import { api } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";
import { BannerImageCropper } from "./BannerImageCropper";
import { useI18n } from "../lib/i18n";
import { useCancelRefresh } from "../lib/useRefreshOwner";

/**
 * v3.9.43 — LinkedIn-style profile surfaces inside the dashboard (no separate page):
 *  • ProfileCard  — compact card that sits ON TOP of the dashboard sidebar menu.
 *  • ProfileHero  — the fuller profile block MERGED at the top of the dashboard's
 *                   main column, above the existing Welcome/stats content.
 * Both render in the current KontrataPH design system (navy/red, card styling).
 */
const BANNER = "bg-gradient-to-br from-navy via-navy/90 to-brand-red/40";

function Avatar({ src, initials, size, ring }: { src?: string | null; initials: string; size: number; ring?: boolean }) {
  return (
    <div className={`rounded-full ${ring ? "ring-4 ring-white" : ""} bg-navy text-white grid place-items-center font-extrabold overflow-hidden shrink-0`}
      style={{ width: size, height: size, fontSize: size / 3 }}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : initials}
    </div>
  );
}

/** Sidebar profile card. Compact clicks open the public profile; default clicks scroll to the merged profile block.
 *  The footer row opens the résumé manager popup. */
export function ProfileCard({ profile, activeResume, onResumeChanged, compact = false }: { profile: any; activeResume?: { fileName: string; url: string } | null; onResumeChanged?: () => void; compact?: boolean }) {
  const { t } = useI18n();
  const [, navigate] = useLocation();
  const [resumesOpen, setResumesOpen] = useState(false);
  function open() {
    navigate("/dashboard");
    setTimeout(() => {
      const el = document.getElementById("dash-profile");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 30);
  }
  const name = profile?.name || "Your profile";
  const initials = profile?.initials || "K";
  if (compact) return <>
    <SidebarIdentityCard name={name} subtitle={profile?.headline || t("dashboard.yourAccount", "Your account")} city={profile?.city}
      cover={profile?.bannerImageUrl ? <img src={api.apiAssetUrl(profile.bannerImageUrl) || undefined} alt="" /> : undefined}
      avatar={profile?.profileImageUrl ? <img src={api.apiAssetUrl(profile.profileImageUrl) || undefined} alt="" /> : <span>{initials}</span>}
      badge={<AccountVerificationBadge {...accountVerificationFrom(profile)} />}
      href={profile?.id ? `/u/${profile.id}?view=public` : "/dashboard"}
      footer={<><button type="button" onClick={() => setResumesOpen(true)} title={activeResume?.fileName || "Add your résumé"}><FileText /><span>{activeResume?.fileName || "Add your résumé"}</span></button><button type="button" className="sidebar-identity-download" aria-label="Manage résumés" onClick={() => setResumesOpen(true)}><Download /></button></>} />
    <ResumeModal open={resumesOpen} onClose={() => setResumesOpen(false)} onChanged={onResumeChanged} />
  </>;
  return (
    <div role="button" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}
      className="card p-0 overflow-hidden mb-2 hover:shadow-lift hover:border-navy-500/60 transition cursor-pointer">
      <div className={`h-16 ${BANNER} relative`}>
        {profile?.bannerImageUrl && <img src={api.apiAssetUrl(profile.bannerImageUrl) || undefined} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      </div>
      <div className="px-3 pb-3 relative z-10">
        <div className="-mt-8 mb-1"><Avatar src={api.apiAssetUrl(profile?.profileImageUrl)} initials={initials} size={60} ring /></div>
        <div className="font-extrabold text-navy text-[15px] leading-tight flex items-center gap-1">
          <span className="truncate">{name}</span><AccountVerificationBadge {...accountVerificationFrom(profile)} />
        </div>
        {profile?.headline && <div className="text-xs text-mut mt-0.5 flex flex-wrap items-center gap-1.5"><span className="line-clamp-2">{profile?.headline}</span></div>}
        {profile?.city && <div className="text-[10px] text-mut mt-1 flex items-center gap-1"><LocationPin className="w-3 h-3" />{profile.city}</div>}
        {/* Active résumé row — opens the résumé manager popup */}
        <div className="mt-2 pt-2 border-t border-line">
          <button type="button" onClick={(e) => { e.stopPropagation(); setResumesOpen(true); }}
            className="w-full flex items-center gap-2 text-left text-navy hover:text-brand-red" title="Manage résumés">
            {activeResume ? (
              <>
                <FileText className="w-4 h-4 shrink-0" />
                <span className="text-[13px] font-semibold truncate flex-1">{activeResume.fileName}</span>
                <Download className="w-3.5 h-3.5 shrink-0 text-mut" />
              </>
            ) : (
              <><Plus className="w-3.5 h-3.5 shrink-0" /><span className="text-xs text-mut flex-1">Add your résumé</span></>
            )}
          </button>
        </div>
      </div>
      <ResumeModal open={resumesOpen} onClose={() => setResumesOpen(false)} onChanged={onResumeChanged} />
    </div>
  );
}

/** Fuller profile header merged at the top of the dashboard main column. */
export function ProfileHero({ profile, userId, onChanged, footer, legacyMobile = false, hasResume = false }: { profile: any; userId: number; onChanged?: () => void; footer?: ReactNode; legacyMobile?: boolean; hasResume?: boolean }) {
  const cancelRefresh = useCancelRefresh();
  const { t } = useI18n();
  const avatarInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  const resumeButtonRef = useRef<HTMLButtonElement>(null);
  const [resumesOpen, setResumesOpen] = useState(false);
  // [#25] Track which image is uploading so the button disables (no re-trigger) and
  // shows a spinner while in flight instead of staying interactable.
  const [busyKind, setBusyKind] = useState<"avatar" | "banner" | null>(null);
  const [removingBanner, setRemovingBanner] = useState(false);
  const [isBannerMenuOpen, setIsBannerMenuOpen] = useState(false);
  const [bannerMenuPosition, setBannerMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
  const [pendingBannerFile, setPendingBannerFile] = useState<File | null>(null);
  const bannerMenuRef = useRef<HTMLDivElement>(null);
  const bannerMenuPopupRef = useRef<HTMLDivElement>(null);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function closeMenus(event: MouseEvent) {
      if (event.target instanceof Node && !bannerMenuRef.current?.contains(event.target) && !bannerMenuPopupRef.current?.contains(event.target)) setIsBannerMenuOpen(false);
      if (event.target instanceof Node && !avatarMenuRef.current?.contains(event.target)) setIsAvatarMenuOpen(false);
    }
    document.addEventListener("mousedown", closeMenus);
    return () => document.removeEventListener("mousedown", closeMenus);
  }, []);
  useEscapeKey(() => {
    const trigger = isBannerMenuOpen ? bannerMenuRef.current : avatarMenuRef.current;
    setIsBannerMenuOpen(false); setIsAvatarMenuOpen(false); setIsAvatarPreviewOpen(false);
    trigger?.querySelector("button")?.focus();
  }, isBannerMenuOpen || isAvatarMenuOpen || isAvatarPreviewOpen);

  async function upload(kind: "avatar" | "banner", file?: File | null) {
    if (!file || busyKind) return;
    cancelRefresh();
    setBusyKind(kind);
    try { await api.uploadProfileImage(kind, file); onChanged?.(); return true; } catch { return false; }
    finally { setBusyKind(null); }
  }

  async function removeBanner() {
    if (!profile?.bannerImageUrl || busyKind || !window.confirm("Remove your profile banner? This cannot be undone.")) return;
    cancelRefresh();
    setRemovingBanner(true);
    setBusyKind("banner");
    try { await api.removeProfileBanner(); onChanged?.(); }
    catch { window.alert("Could not remove the banner. Please try again."); }
    finally { setBusyKind(null); setRemovingBanner(false); }
  }

  const skills: string[] = Array.isArray(profile?.skills) ? profile.skills : [];
  const profileImageSrc = api.apiAssetUrl(profile?.profileImageUrl);
  return (
    <>
      <div id="dash-profile" data-refresh-mutation={!!busyKind || undefined} className="card p-0 overflow-visible mb-5 dashboard-profile-hero">
      <div className={`h-28 overflow-hidden rounded-t-[inherit] sm:h-32 ${BANNER} relative dashboard-profile-banner`}>
        {profile?.bannerImageUrl && <img src={api.apiAssetUrl(profile.bannerImageUrl) || undefined} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <input ref={bannerInput} type="file" accept="image/*" className="hidden" onChange={(e) => { setPendingBannerFile(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
        <div ref={bannerMenuRef} className="absolute top-4 right-4 z-20">
          <button type="button" onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            setBannerMenuPosition({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
            setIsBannerMenuOpen((isOpen) => !isOpen);
          }} disabled={!!busyKind}
            className="h-[38px] w-[38px] rounded-full bg-white/90 text-navy shadow grid place-items-center hover:bg-white disabled:opacity-60"
            aria-label="Banner actions" aria-expanded={isBannerMenuOpen} aria-haspopup="menu">
            {busyKind === "banner" ? <Loader2 className="w-4 h-4 animate-spin" /> : <EllipsisVertical className="w-5 h-5" />}
          </button>
          {isBannerMenuOpen && bannerMenuPosition && createPortal(
            <div ref={bannerMenuPopupRef} role="menu" style={{ position: "fixed", top: bannerMenuPosition.top, right: bannerMenuPosition.right }} className="z-[100] w-[min(240px,calc(100vw-1rem))] rounded-[10px] border border-line bg-white p-1 shadow-lift">
              <Link href={`/u/${userId}?view=public`} role="menuitem" onClick={() => setIsBannerMenuOpen(false)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-sm font-medium text-navy hover:bg-navy-50">
                <Eye className="w-4 h-4" />Public Profile
              </Link>
              <Link href="/worker-profile" role="menuitem" onClick={() => setIsBannerMenuOpen(false)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-sm font-medium text-navy hover:bg-navy-50">
                <Pencil className="w-4 h-4" />Edit profile
              </Link>
              <button type="button" role="menuitem" onClick={() => { setIsBannerMenuOpen(false); bannerInput.current?.click(); }} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm font-medium text-navy hover:bg-navy-50">
                <Camera className="w-4 h-4" />Edit banner
              </button>
              {profile?.bannerImageUrl && <button type="button" role="menuitem" onClick={() => { setIsBannerMenuOpen(false); void removeBanner(); }} disabled={!!busyKind} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm font-medium text-brand-red hover:bg-brand-red/5 disabled:opacity-60">
                {removingBanner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {removingBanner ? "Removing…" : "Remove banner"}
              </button>}
            </div>, document.body,
          )}
        </div>
      </div>
      <div className="px-5 pb-5 relative z-10 dashboard-profile-body">
        <div className="flex flex-col sm:flex-row gap-3 dashboard-profile-identity">
          <div ref={avatarMenuRef} className="relative -mt-11 shrink-0">
            <input ref={avatarInput} type="file" accept="image/*" className="hidden" onChange={(e) => { upload("avatar", e.target.files?.[0]); e.currentTarget.value = ""; }} />
            <button type="button" onClick={() => setIsAvatarMenuOpen((isOpen) => !isOpen)} className="rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-red/50" aria-label="Profile picture actions" aria-expanded={isAvatarMenuOpen} aria-haspopup="menu">
              <Avatar src={profileImageSrc} initials={profile?.initials || "K"} size={92} ring />
            </button>
            {isAvatarMenuOpen && (
              <div role="menu" className="absolute left-0 top-full z-20 mt-3 w-64 max-w-[calc(100vw-2.5rem)] rounded-xl border border-line bg-white p-2 shadow-lift">
                <button type="button" role="menuitem" onClick={() => { setIsAvatarMenuOpen(false); setIsAvatarPreviewOpen(true); }} disabled={!profileImageSrc}
                  className="flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-base font-medium text-navy hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <Eye className="w-5 h-5" />View profile picture
                </button>
                <button type="button" role="menuitem" onClick={() => { setIsAvatarMenuOpen(false); avatarInput.current?.click(); }} disabled={!!busyKind}
                  className="flex h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-base font-medium text-navy hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-60">
                  {busyKind === "avatar" ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}Select profile picture
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 sm:pt-3">
            <div className="text-xl font-extrabold text-navy flex items-center gap-2">{profile?.name || "Your profile"}<AccountVerificationBadge {...accountVerificationFrom(profile)} /></div>
            {profile?.headline && <div className="text-sm text-mut font-medium flex flex-wrap items-center gap-2">{profile?.headline && <span>{profile.headline}</span>}</div>}
            {profile?.city && <div className="text-[11px] text-mut flex items-center gap-1 mt-0.5"><LocationPin className="w-3.5 h-3.5" />{profile.city}</div>}
          </div>
        </div>
        {legacyMobile && <button ref={resumeButtonRef} type="button" onClick={() => setResumesOpen(true)} className="btn-outline mt-4 min-h-11 w-full">
          <FileText className="h-4 w-4" aria-hidden="true" />{t(hasResume ? "profile.updateResume" : "profile.addResume")}
        </button>}
        {legacyMobile && profile?.bio && <p className="text-sm text-mut mt-4 whitespace-pre-line max-w-3xl">{profile.bio}</p>}
        {skills.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {skills.slice(0, 10).map((s) => <span key={s} className="chip bg-navy-50 text-navy-600">{s}</span>)}
          </div>
        )}
      </div>
      {footer}
      </div>
      <ResumeModal open={resumesOpen} onClose={() => { setResumesOpen(false); resumeButtonRef.current?.focus(); }} onChanged={onChanged} />
      <BannerImageCropper file={pendingBannerFile} onCancel={() => setPendingBannerFile(null)} onApply={async (image) => {
        const croppedFile = new File([image], "profile-cover.jpg", { type: "image/jpeg" });
        if (!await upload("banner", croppedFile)) throw new Error("Cover upload failed");
        setPendingBannerFile(null);
      }} />
      {isAvatarPreviewOpen && profileImageSrc && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Profile picture" onClick={() => setIsAvatarPreviewOpen(false)}>
          <div className="relative max-h-full max-w-full" onClick={(event) => event.stopPropagation()}>
            <img src={profileImageSrc} alt={`${profile?.name || "Your"} profile picture`} className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain" />
            <button type="button" onClick={() => setIsAvatarPreviewOpen(false)} className="absolute -right-3 -top-3 grid h-9 w-9 place-items-center rounded-full bg-white text-navy shadow-lift" aria-label="Close profile picture preview">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
